import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import axios from 'axios'

import { MUIButton } from 'components/common/MUIButton'
import { MUIInputField } from 'components/common/MUIInputField'
import { MUISwitch } from 'components/common/MUISwitch'
import { DropdownInputTypes, MUIDropdownInput } from 'components/common/MUIDropdownInput/MUIDropdownInput.component'
import { useAuth0 } from 'utils/auth'
import { GRAY_700 } from 'constants/styling/theme'
import { PfInlineLoading } from '../components/PfInlineLoading.component'
import { useGetOrganization$, useGetUserClient$ } from 'dataQueries'
import { PFClientSelectionError } from 'constants/purchaseFlow'
import { usePfStore } from 'components/pages/PurchaseFlowRevamp/store'
import {
  PfRevampEventName,
  usePfRevampAnalytics
} from 'components/pages/PurchaseFlowRevamp/analytics'
import { UserIsPlatformUserClient } from 'utils/typeguards'
import { FINISHED_QUERY_STATES } from 'constants/misc'
import { CommonStatusCode } from 'constants/API'
import { logger } from 'utils/logging'

import { workspaceHasPaidEntitlement } from './workspaceEntitlement'
import { activeOrderableWorkspaces } from './useAdminTargetRevalidation'

/** Admin target resolved by this section. */
export interface ClientSelection {
  /** Looked-up client email — required by the PF v2 create-session admin body. */
  email: string
  /** Workspace the order will be placed in. */
  workspaceId: string
  /** Display name of the selected workspace (not persisted in the store). */
  workspaceName: string
}

interface Props {
  /** Fired after the admin target has been persisted into the store. */
  onSelected?: (selection: ClientSelection) => void
  /** When provided, renders a cancel affordance (the "change client" flow). */
  onCancel?: () => void
}

/**
 * Admin-only client lookup: email search + workspace picker + Continue.
 *
 * Rendered as the first STAGE of `PropertySelectionDrawer` (it replaced the
 * standalone `select-user` page — the gate is now a content swap inside the
 * same drawer, so every drawer mount point inherits it). On Continue it stores
 * targetUserId + targetWorkspaceId + targetUserEmail in the propertySlice and
 * hands control back to the caller via `onSelected`; navigation is the caller's
 * business.
 *
 * Client roles render nothing — the caller gates on `roles.isAdmin`, this is a
 * defensive second check.
 */
export const ClientSelectionSection: React.FC<Props> = ({ onSelected, onCancel }) => {
  const { t } = useTranslation(['purchase_flow_revamp', 'purchase_flow', 'common'])
  const { roles } = useAuth0()

  const setAdminTarget = usePfStore((s) => s.setAdminTarget)
  const setAdminSkips = usePfStore((s) => s.setAdminSkips)
  const trackAnalytics = usePfRevampAnalytics()

  const [emailInput, setEmailInput] = useState('')
  const [submittedEmail, setSubmittedEmail] = useState('')
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState('')
  // A resumed admin order reopens the gate with the automation it already skips.
  const [skipCtAutomation, setSkipCtAutomation] = useState(() => usePfStore.getState().adminSkipCtAutomation)
  const [skipExternalProviderSending, setSkipExternalProviderSending] = useState(() => usePfStore.getState().adminSkipExternalProviderSending)

  const getPFClient = useGetUserClient$()
  const getPFOrganization = useGetOrganization$()

  // Trigger client lookup when email is searched
  const handleSearch = useCallback(() => {
    const trimmed = emailInput.trim()
    if (!trimmed) return
    setSubmittedEmail(trimmed)
    setSelectedWorkspaceId('')
    getPFClient.mutate({ email: trimmed })
  }, [emailInput, getPFClient])

  const clientOrganizationId = useMemo(() => {
    const userData = getPFClient.data?.data
    return userData && UserIsPlatformUserClient(userData) ? userData.organizationId ?? '' : ''
  }, [getPFClient.data?.data])

  // Re-fetch org when client data resolves
  useEffect(() => {
    if (clientOrganizationId) getPFOrganization.mutate({ organizationId: clientOrganizationId })
  }, [getPFClient.data?.data]) // eslint-disable-line react-hooks/exhaustive-deps

  // Shared with the resume-time revalidation so the set offered here and the set
  // a stored workspace is checked against can never drift apart.
  const activeClientWorkspaces = useMemo(
    () => activeOrderableWorkspaces(getPFClient.data?.data),
    [getPFClient.data?.data]
  )

  // Auto-select when only one workspace available
  useEffect(() => {
    if (activeClientWorkspaces.length === 1) {
      setSelectedWorkspaceId(activeClientWorkspaces[0].id)
    }
  }, [activeClientWorkspaces])

  const clientSelectionError = useMemo<PFClientSelectionError | undefined>(() => {
    if (!submittedEmail) return undefined
    if (!FINISHED_QUERY_STATES.has(getPFClient.status)) return PFClientSelectionError.NO_DATA

    if (getPFClient.isError) {
      // Use isAxiosError type guard instead of `as any` cast.
      const status = axios.isAxiosError(getPFClient.error)
        ? getPFClient.error.response?.status
        : undefined
      if (status === CommonStatusCode.NOT_FOUND) return PFClientSelectionError.NOT_FOUND
    }

    const userData = getPFClient.data?.data
    if (!userData) return PFClientSelectionError.NOT_FOUND

    const responseStatus = getPFClient.data?.status
    if (responseStatus === 422) return PFClientSelectionError.NOT_CLIENT

    if (!UserIsPlatformUserClient(userData)) return undefined

    return undefined
  }, [submittedEmail, getPFClient.status, getPFClient.isError, getPFClient.error, getPFClient.data])

  const errorMessage = useMemo(() => {
    switch (clientSelectionError) {
      case PFClientSelectionError.NOT_FOUND:
        return t('purchase_flow_revamp:admin_gate.user_selection_error_not_found')
      case PFClientSelectionError.NOT_CLIENT:
        return t('purchase_flow_revamp:admin_gate.user_selection_error_not_client')
      default:
        return undefined
    }
  }, [clientSelectionError, t])

  const clientData = getPFClient.data?.data
  const targetUserId = useMemo(() => {
    if (!clientData || !UserIsPlatformUserClient(clientData)) return ''
    return clientData.id ?? ''
  }, [clientData])

  // This lookup is the only place the target client's own name and phone are
  // ever fetched, and it is a mutation — the data dies with this component.
  // Capturing them now is what lets the instructions step prefill the meeting
  // contact with the CLIENT's details instead of just their email.
  const targetContact = useMemo(() => {
    if (!clientData || !UserIsPlatformUserClient(clientData)) return {}
    return { name: clientData.name, phone: clientData.phone, internalId: clientData.internalId }
  }, [clientData])

  // The subscription-gated features (favourite creative, fast delivery)
  // must be judged on the CLIENT's selected workspace, so the entitlement is
  // resolved here where that workspace is known, not from the admin's own.
  const selectedWorkspaceIsSubscribed = useMemo(
    () => workspaceHasPaidEntitlement(activeClientWorkspaces.find((ws) => ws.id === selectedWorkspaceId)),
    [activeClientWorkspaces, selectedWorkspaceId]
  )

  // Legacy's invoice gate read the TARGET's permission, and the organization is
  // already fetched here for exactly this kind of question.
  // A client without an organization has no permission to read: the mutation still holds the previous client's
  // organization, which must not leak into this one.
  const targetInvoiceAllowed = useMemo(
    () => (clientOrganizationId ? getPFOrganization.data?.data?.invoiceAllowed ?? null : null),
    [clientOrganizationId, getPFOrganization.data?.data?.invoiceAllowed]
  )
  // Unlike the invoice permission, an unknown PoA requirement is unsafe: read as "owners only", DocEstate refuses the
  // order when the client's organization needs the representatives' PoA too (QA-602). So Continue waits for the
  // organization, and a client without one has no requirement.
  const targetRequiresPoaRepresentatives = useMemo(
    () => {
      if (!clientOrganizationId) return false
      return getPFOrganization.isSuccess ? !!getPFOrganization.data?.data?.requirePowerOfAttorneyRepresentatives : null
    },
    [clientOrganizationId, getPFOrganization.isSuccess, getPFOrganization.data?.data?.requirePowerOfAttorneyRepresentatives]
  )
  const isOrganizationPending = !!clientOrganizationId && !getPFOrganization.isSuccess && !getPFOrganization.isError
  const isClientDataIncomplete = !!targetUserId && (
    !targetContact.internalId || (!!clientOrganizationId && getPFOrganization.isError)
  )

  useEffect(() => {
    if (!isClientDataIncomplete) return
    logger.warn('pf_revamp admin gate: client data incomplete', undefined, { context: { component: 'ClientSelectionSection', action: 'admin_gate', metadata: {
      targetUserId,
      hasInternalId: !!targetContact.internalId,
      organizationFailed: getPFOrganization.isError,
    } } })
  }, [isClientDataIncomplete]) // eslint-disable-line react-hooks/exhaustive-deps

  const canContinue = !!targetUserId && !!selectedWorkspaceId && !clientSelectionError
    && !isOrganizationPending && !isClientDataIncomplete

  const handleContinue = useCallback(() => {
    if (!canContinue) return
    // `submittedEmail` is the looked-up client's email — required by the PF v2
    // create-session admin body (`useEnsurePfV2Session`).
    setAdminSkips({ ctAutomation: skipCtAutomation, externalProviderSending: skipExternalProviderSending })
    setAdminTarget(targetUserId, selectedWorkspaceId, { ...targetContact, email: submittedEmail, isSubscribed: selectedWorkspaceIsSubscribed, invoiceAllowed: targetInvoiceAllowed, requiresPoaRepresentatives: targetRequiresPoaRepresentatives })
    trackAnalytics({
      type: PfRevampEventName.ADMIN_TARGET_SET,
      payload: {
        targetUserId,
        targetWorkspaceId: selectedWorkspaceId,
        invoiceAllowed: targetInvoiceAllowed,
        requiresPoaRepresentatives: targetRequiresPoaRepresentatives,
        skipCtAutomation,
        skipExternalProviderSending,
      },
    })
    onSelected?.({
      email: submittedEmail,
      workspaceId: selectedWorkspaceId,
      workspaceName: activeClientWorkspaces.find((ws) => ws.id === selectedWorkspaceId)?.name ?? '',
    })
  }, [canContinue, setAdminSkips, skipCtAutomation, skipExternalProviderSending, setAdminTarget, targetUserId, selectedWorkspaceId, submittedEmail, targetContact, selectedWorkspaceIsSubscribed, targetInvoiceAllowed, targetRequiresPoaRepresentatives, activeClientWorkspaces, trackAnalytics, onSelected])

  // Non-admin users never see this stage (the drawer gates on the role), but a
  // defensive check prevents a confusing half-rendered form.
  if (!roles.isAdmin) return null

  const isSearching = getPFClient.isPending && !!submittedEmail
  const showWorkspacePicker = !isSearching && !clientSelectionError && activeClientWorkspaces.length > 0

  return (
    <Stack spacing={0} data-cy="pf-revamp-admin-gate">

      {/** Email search */}
      <Stack spacing={0} mb={2}>

        <Typography variant="text-sm" fontWeight={500} color={GRAY_700} mb={0.6}>
          {t('purchase_flow_revamp:admin_gate.user_label')}
        </Typography>

        <Stack direction="row" spacing={0} gap={0.8} alignItems="flex-start">

          <Box flex={1} data-cy="pf-revamp-admin-gate-email-wrap">
            <MUIInputField
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSearch() }}
              placeholder={t('purchase_flow_revamp:admin_gate.user_placeholder')}
            />
          </Box>

          <Box data-cy="pf-revamp-admin-gate-search-wrap">
            <MUIButton
              size="md"
              type="secondaryBorder"
              onClick={handleSearch}
              disabled={!emailInput.trim()}
            >
              {t('purchase_flow_revamp:admin_gate.search_button')}
            </MUIButton>
          </Box>

        </Stack>

        {isSearching &&
          <Box mt={0.8}>
            <PfInlineLoading size="xs" message={t('purchase_flow_revamp:admin_gate.searching')} />
          </Box>
        }

        {!!errorMessage &&
          <Typography variant="text-xs" color="error.main" mt={0.6}>
            {errorMessage}
          </Typography>
        }

      </Stack>

      {/** Workspace picker */}
      {showWorkspacePicker &&
        <Stack spacing={0} mb={2.4}>

          <Typography variant="text-sm" fontWeight={500} color={GRAY_700} mb={0.6}>
            {t('purchase_flow_revamp:admin_gate.workspace_label')}
          </Typography>

          <MUIDropdownInput
            isNativeSelect
            type={DropdownInputTypes.SELECTOR}
            value={selectedWorkspaceId}
            placeholder={t('purchase_flow_revamp:admin_gate.workspace_placeholder')}
            onChange={(e) => setSelectedWorkspaceId(e.target.value)}
          >

            <option value="" disabled>
              {t('purchase_flow_revamp:admin_gate.workspace_placeholder')}
            </option>

            {activeClientWorkspaces.map((ws) => (
              <option key={ws.id} value={ws.id}>
                {ws.name}
              </option>
            ))}

          </MUIDropdownInput>

          <Stack gap={1.2} mt={1.6} alignItems="flex-start" data-cy="pf-revamp-admin-gate-skips">
            <MUISwitch
              label={t('purchase_flow:client_step.skip_ct_automation')}
              labelPlacement="end"
              checked={skipCtAutomation}
              onChange={() => setSkipCtAutomation((prev) => !prev)}
            />
            <MUISwitch
              label={t('purchase_flow:client_step.skip_external_provider_automation')}
              labelPlacement="end"
              checked={skipExternalProviderSending}
              onChange={() => setSkipExternalProviderSending((prev) => !prev)}
            />
          </Stack>

        </Stack>
      }

      {isClientDataIncomplete &&
        <Typography variant="text-xs" color="error.main" mb={1.6} data-cy="pf-revamp-admin-gate-client-data-error">
          {t('purchase_flow_revamp:admin_gate.client_data_error')}
        </Typography>
      }

      <Stack direction="row" justifyContent="flex-end" gap={0.8}>

        {!!onCancel &&
          <Box data-cy="pf-revamp-admin-gate-cancel-wrap">
            <MUIButton
              size="md"
              type="secondaryBorder"
              onClick={onCancel}
            >
              {t('common:Cancel')}
            </MUIButton>
          </Box>
        }

        <Box data-cy="pf-revamp-admin-gate-continue-wrap">
          <MUIButton
            size="md"
            disabled={!canContinue}
            onClick={handleContinue}
          >
            {t('purchase_flow_revamp:admin_gate.continue_button')}
          </MUIButton>
        </Box>

      </Stack>

    </Stack>
  )
}
